import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { useLocation } from '../../contexts/LocationContext'

// PLE data processing utilities
const GRADE_DIVISIONS = [
  'Division 1',
  'Division 2',
  'Division 3',
  'Division 4',
  'Division U',
  'Division X',
]
const PASS_GRADES = ['Division 1', 'Division 2', 'Division 3']
const EXCELLENCE_GRADES = ['Division 1', 'Division 2']

// Calculate pass rates and other metrics from PLE data
function calculatePLEMetrics(data) {
  const groupedData = {}

  // Group by year, district, school, gender
  data.forEach(record => {
    const key = `${record.year}-${record.district}-${record.school_name}-${record.gender}`
    if (!groupedData[key]) {
      groupedData[key] = {
        year: parseInt(record.year),
        district: record.district,
        subcounty: record.subcounty,
        parish: record.parish,
        school: record.school_name,
        gender: record.gender,
        ownership: record.ownership,
        location_code: record.location_code,
        grades: {},
      }
    }

    const grade = record.grade
    const value = parseFloat(record.value) || 0
    groupedData[key].grades[grade] =
      (groupedData[key].grades[grade] || 0) + value
  })

  // Calculate metrics for each group
  return Object.values(groupedData).map(group => {
    const total = GRADE_DIVISIONS.reduce(
      (sum, grade) => sum + (group.grades[grade] || 0),
      0
    )
    const passCount = PASS_GRADES.reduce(
      (sum, grade) => sum + (group.grades[grade] || 0),
      0
    )
    const excellenceCount = EXCELLENCE_GRADES.reduce(
      (sum, grade) => sum + (group.grades[grade] || 0),
      0
    )

    return {
      ...group,
      total,
      passRate: total > 0 ? (passCount / total) * 100 : 0,
      excellenceRate: total > 0 ? (excellenceCount / total) * 100 : 0,
      failureRate:
        total > 0
          ? (((group.grades['Division U'] || 0) +
              (group.grades['Division X'] || 0)) /
              total) *
            100
          : 0,
    }
  })
}

// Aggregate data for visualization
function aggregateByYear(processedData) {
  const yearData = {}

  processedData.forEach(record => {
    // Validate record has required properties
    if (
      !record.year ||
      !record.grades ||
      !record.gender ||
      !record.ownership ||
      !record.total
    ) {
      return // Skip invalid records
    }

    const year = record.year
    if (!yearData[year]) {
      yearData[year] = {
        year,
        total: 0,
        grades: {},
        byGender: {
          Male: { total: 0, grades: {} },
          Female: { total: 0, grades: {} },
        },
        byOwnership: {
          Government: { total: 0, grades: {} },
          Private: { total: 0, grades: {} },
        },
      }
      GRADE_DIVISIONS.forEach(grade => {
        yearData[year].grades[grade] = 0
        yearData[year].byGender.Male.grades[grade] = 0
        yearData[year].byGender.Female.grades[grade] = 0
        yearData[year].byOwnership.Government.grades[grade] = 0
        yearData[year].byOwnership.Private.grades[grade] = 0
      })
    }

    const yearRecord = yearData[year]
    yearRecord.total += record.total || 0

    // Ensure gender and ownership categories exist
    if (!yearRecord.byGender[record.gender]) {
      yearRecord.byGender[record.gender] = { total: 0, grades: {} }
      GRADE_DIVISIONS.forEach(grade => {
        yearRecord.byGender[record.gender].grades[grade] = 0
      })
    }

    if (!yearRecord.byOwnership[record.ownership]) {
      yearRecord.byOwnership[record.ownership] = { total: 0, grades: {} }
      GRADE_DIVISIONS.forEach(grade => {
        yearRecord.byOwnership[record.ownership].grades[grade] = 0
      })
    }

    GRADE_DIVISIONS.forEach(grade => {
      const count = record.grades[grade] || 0
      yearRecord.grades[grade] += count
      yearRecord.byGender[record.gender].grades[grade] += count
      yearRecord.byOwnership[record.ownership].grades[grade] += count
    })

    yearRecord.byGender[record.gender].total += record.total || 0
    yearRecord.byOwnership[record.ownership].total += record.total || 0
  })

  return Object.values(yearData).sort((a, b) => a.year - b.year)
}

// Process data for Pass Rate Heatmap by Location
function processPassRateHeatmap(processedData, locationLevel) {
  const heatmapData = {}

  processedData.forEach(record => {
    if (!record[locationLevel]) return

    const location = record[locationLevel]
    const year = record.year
    const key = `${location}-${year}`

    if (!heatmapData[key]) {
      heatmapData[key] = {
        location,
        year,
        passCount: 0,
        total: 0,
      }
    }

    const passCount = PASS_GRADES.reduce(
      (sum, grade) => sum + (record.grades[grade] || 0),
      0
    )
    heatmapData[key].passCount += passCount
    heatmapData[key].total += record.total || 0
  })

  // Calculate pass rates and organize data
  const locations = new Set()
  const years = new Set()
  const data = []

  Object.values(heatmapData).forEach(record => {
    const passRate =
      record.total > 0 ? (record.passCount / record.total) * 100 : 0

    locations.add(record.location)
    years.add(record.year)

    data.push({
      location: record.location,
      year: record.year,
      passRate,
      color:
        passRate >= 80 ? '#10b981' : passRate >= 60 ? '#f59e0b' : '#ef4444',
    })
  })

  return {
    locations: Array.from(locations).sort(),
    years: Array.from(years).sort((a, b) => a - b),
    data,
  }
}

// Process data for Failure Rate Heatmap by Location
function processFailureRateHeatmap(processedData, locationLevel) {
  const heatmapData = {}

  processedData.forEach(record => {
    if (!record[locationLevel]) return

    const location = record[locationLevel]
    const year = record.year
    const key = `${location}-${year}`

    if (!heatmapData[key]) {
      heatmapData[key] = {
        location,
        year,
        failureCount: 0,
        total: 0,
      }
    }

    const failureCount =
      (record.grades['Division U'] || 0) + (record.grades['Division X'] || 0)
    heatmapData[key].failureCount += failureCount
    heatmapData[key].total += record.total || 0
  })

  // Calculate failure rates and organize data
  const locations = new Set()
  const years = new Set()
  const data = []

  Object.values(heatmapData).forEach(record => {
    const failureRate =
      record.total > 0 ? (record.failureCount / record.total) * 100 : 0

    locations.add(record.location)
    years.add(record.year)

    data.push({
      location: record.location,
      year: record.year,
      failureRate,
      color:
        failureRate < 10 ? '#10b981' : failureRate < 20 ? '#f59e0b' : '#ef4444',
    })
  })

  return {
    locations: Array.from(locations).sort(),
    years: Array.from(years).sort((a, b) => a - b),
    data,
  }
}

// Process data for School Performance Trajectories
function processSchoolTrajectories(processedData) {
  const schoolData = {}

  processedData.forEach(record => {
    if (!record.school) return

    const school = record.school
    const year = record.year
    const key = `${school}-${year}`

    if (!schoolData[key]) {
      schoolData[key] = {
        school,
        year,
        excellenceCount: 0,
        total: 0,
      }
    }

    const excellenceCount = EXCELLENCE_GRADES.reduce(
      (sum, grade) => sum + (record.grades[grade] || 0),
      0
    )
    schoolData[key].excellenceCount += excellenceCount
    schoolData[key].total += record.total || 0
  })

  // Group by school and calculate trajectories
  const schoolTrajectories = {}
  Object.values(schoolData).forEach(record => {
    if (!schoolTrajectories[record.school]) {
      schoolTrajectories[record.school] = []
    }

    const excellenceRate =
      record.total > 0 ? (record.excellenceCount / record.total) * 100 : 0
    schoolTrajectories[record.school].push({
      year: record.year,
      excellenceRate,
      total: record.total,
    })
  })

  // Calculate growth rate for each school
  const schoolGrowthRates = Object.entries(schoolTrajectories)
    .map(([school, trajectory]) => {
      trajectory.sort((a, b) => a.year - b.year)

      if (trajectory.length < 2) return null // Need at least 2 points

      const firstRate = trajectory[0].excellenceRate
      const lastRate = trajectory[trajectory.length - 1].excellenceRate
      const totalStudents = trajectory.reduce((sum, t) => sum + t.total, 0)

      // Calculate growth rate (percentage points per year)
      const growthRate = (lastRate - firstRate) / (trajectory.length - 1)

      return {
        school,
        trajectory,
        growthRate,
        firstRate,
        lastRate,
        totalStudents,
        isImproving: growthRate > 0,
        isStable: Math.abs(growthRate) <= 1,
        isDecining: growthRate < -1,
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.growthRate - a.growthRate)
    .slice(0, 12) // Top 12 by growth rate

  return schoolGrowthRates
}

function PLETrends({ data }) {
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
  } = useLocation()
  // Process data for visualizations
  const processedData = useMemo(() => {
    if (!data || !data.length) return []
    return calculatePLEMetrics(data)
  }, [data])

  const yearlyData = useMemo(() => {
    return aggregateByYear(processedData)
  }, [processedData])

  // Determine location level for heatmaps based on current selection (show NEXT level)
  const locationLevel = useMemo(() => {
    if (selectedVillage) return null // No sub-levels below village
    if (selectedParish) return 'village' // Show villages within parish
    if (selectedSubcounty) return 'parish' // Show parishes within subcounty
    if (selectedDistrict) return 'subcounty' // Show subcounties within district
    return 'district' // Show districts when viewing all
  }, [selectedDistrict, selectedSubcounty, selectedParish, selectedVillage])

  // Process data for new visualizations
  const passRateHeatmapData = useMemo(() => {
    if (!processedData.length || !locationLevel) return null
    return processPassRateHeatmap(processedData, locationLevel)
  }, [processedData, locationLevel])

  const failureRateHeatmapData = useMemo(() => {
    if (!processedData.length || !locationLevel) return null
    return processFailureRateHeatmap(processedData, locationLevel)
  }, [processedData, locationLevel])

  const schoolTrajectoriesData = useMemo(() => {
    if (!processedData.length) return []
    return processSchoolTrajectories(processedData)
  }, [processedData])

  // 1. PLE Performance Trends Over Time (Stacked Area Chart)
  const performanceTrendsOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)
    const colors = {
      'Division 1': '#10b981', // Green
      'Division 2': '#34d399', // Light Green
      'Division 3': '#fbbf24', // Yellow
      'Division 4': '#fb923c', // Orange
      'Division U': '#f87171', // Light Red
      'Division X': '#dc2626', // Red
    }

    return {
      title: {
        text: 'PLE Performance Trends Over Time',
        show: false,
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          const total = params.reduce(
            (sum, param) => sum + (parseFloat(param.value) || 0),
            0
          )
          params.forEach(param => {
            const numValue = parseFloat(param.value) || 0
            const percentage =
              total > 0 ? ((numValue / total) * 100).toFixed(1) : '0.0'
            result += `${param.marker} ${param.seriesName}: ${numValue} (${percentage}%)<br/>`
          })
          result += `<strong>Total Students: ${total}</strong>`
          return result
        },
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      legend: {
        data: GRADE_DIVISIONS,
        top: 40,
        textStyle: {
          fontSize: 12,
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        nameLocation: 'middle',
        nameGap: 25,
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Number of Students',
        nameLocation: 'middle',
        nameGap: 60,
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      series: GRADE_DIVISIONS.map(grade => ({
        name: grade,
        type: 'bar',
        stack: 'performance',
        data: yearlyData.map(d => d.grades[grade] || 0),
        itemStyle: { color: colors[grade] },
        emphasis: { focus: 'series' },
      })),
    }
  }, [yearlyData])

  // 2. Gender Performance Gap Analysis
  const genderGapOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)

    const maleExcellence = yearlyData.map(d => {
      const total = d.byGender.Male.total
      const excellence =
        (d.byGender.Male.grades['Division 1'] || 0) +
        (d.byGender.Male.grades['Division 2'] || 0)
      return total > 0 ? (excellence / total) * 100 : 0
    })

    const femaleExcellence = yearlyData.map(d => {
      const total = d.byGender.Female.total
      const excellence =
        (d.byGender.Female.grades['Division 1'] || 0) +
        (d.byGender.Female.grades['Division 2'] || 0)
      return total > 0 ? (excellence / total) * 100 : 0
    })

    const genderGap = maleExcellence.map(
      (male, i) => male - femaleExcellence[i]
    )

    return {
      title: {
        show: false,
        text: 'Gender Performance Gap Analysis',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            const numValue = parseFloat(param.value) || 0
            if (param.seriesName === 'Gender Gap') {
              const value =
                numValue > 0
                  ? `+${numValue.toFixed(1)}% (Male advantage)`
                  : `${numValue.toFixed(1)}% (Female advantage)`
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`
            } else {
              result += `${param.marker} ${param.seriesName}: ${numValue.toFixed(1)}%<br/>`
            }
          })
          return result
        },
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      legend: {
        data: ['Male Excellence Rate', 'Female Excellence Rate', 'Gender Gap'],
        top: 40,
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        nameGap: 25,
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Excellence Rate (%)',
          nameLocation: 'middle',
          nameGap: 30,
          max: 100,
          splitLine: {
            show: false,
          },
          nameTextStyle: {
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
        {
          type: 'value',
          name: 'Gender Gap (%)',
          nameLocation: 'middle',
          nameGap: 30,
          splitLine: { show: false },
          nameTextStyle: {
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
      ],
      series: [
        {
          name: 'Male Excellence Rate',
          type: 'bar',
          barGap: 0,
          data: maleExcellence.map(v => v.toFixed(1)),
          itemStyle: { color: '#3b82f6' },
        },
        {
          name: 'Female Excellence Rate',
          type: 'bar',
          barGap: 0,
          data: femaleExcellence.map(v => v.toFixed(1)),
          itemStyle: { color: '#ec4899' },
        },
        {
          name: 'Gender Gap',
          type: 'line',
          yAxisIndex: 1,
          data: genderGap.map(v => v.toFixed(1)),
          lineStyle: { color: '#f59e0b', width: 3 },
          itemStyle: { color: '#f59e0b' },
          symbol: 'circle',
          symbolSize: 8,
        },
      ],
    }
  }, [yearlyData])

  // 3. School Ownership Impact Analysis
  const ownershipImpactOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)

    const govExcellence = yearlyData.map(d => {
      const total = d.byOwnership.Government.total
      const excellence =
        (d.byOwnership.Government.grades['Division 1'] || 0) +
        (d.byOwnership.Government.grades['Division 2'] || 0)
      return total > 0 ? (excellence / total) * 100 : 0
    })

    const privateExcellence = yearlyData.map(d => {
      const total = d.byOwnership.Private.total
      const excellence =
        (d.byOwnership.Private.grades['Division 1'] || 0) +
        (d.byOwnership.Private.grades['Division 2'] || 0)
      return total > 0 ? (excellence / total) * 100 : 0
    })

    return {
      title: {
        show: false,
        text: 'School Ownership Impact on Performance',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            const numValue = parseFloat(param.value) || 0
            result += `${param.marker} ${param.seriesName}: ${numValue}% excellence rate<br/>`
          })
          return result
        },
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      legend: {
        data: ['Government Schools', 'Private Schools'],
        top: 40,
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Excellence Rate (%)',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        max: 100,
        splitLine: {
          show: false,
        },
      },
      series: [
        {
          name: 'Government Schools',
          type: 'line',
          data: govExcellence.map(v => v.toFixed(1)),
          lineStyle: { color: '#10b981', width: 3 },
          itemStyle: { color: '#10b981' },
          symbol: 'circle',
          symbolSize: 8,
          smooth: true,
        },
        {
          name: 'Private Schools',
          type: 'line',
          data: privateExcellence.map(v => v.toFixed(1)),
          lineStyle: { color: '#8b5cf6', width: 3 },
          itemStyle: { color: '#8b5cf6' },
          symbol: 'circle',
          symbolSize: 8,
          smooth: true,
        },
      ],
    }
  }, [yearlyData])

  // 4. Dynamic Location Performance Ranking Evolution
  const locationRankingOption = useMemo(() => {
    if (!processedData.length) return {}

    // Determine what location level to show based on current selection
    let locationField, chartTitle
    if (selectedVillage) {
      // If village selected, no sub-levels to show
      return {
        title: {
          show: false,
          text: 'School Performance Ranking Evolution',
          left: 'center',
          textStyle: { fontSize: 16, fontWeight: 'bold' },
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'center',
          style: {
            text: 'Individual school rankings\nwithin village coming soon',
            fontSize: 16,
            fill: '#666',
            textAlign: 'center',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
      }
    } else if (selectedParish) {
      locationField = 'village'
    } else if (selectedSubcounty) {
      locationField = 'parish'
    } else if (selectedDistrict) {
      locationField = 'subcounty'
    } else {
      locationField = 'district'
    }

    // Aggregate data by the determined location field
    const locationPerformance = {}
    processedData.forEach(record => {
      if (!record.location_code) return

      const locationName = record[locationField] || `Unknown ${locationField}`
      const year = record.year
      const key = `${locationName}-${year}`

      if (!locationPerformance[key]) {
        locationPerformance[key] = {
          location: locationName,
          year,
          total: 0,
          grades: {},
        }
      }

      // Aggregate data
      locationPerformance[key].total += record.total
      GRADE_DIVISIONS.forEach(grade => {
        if (!locationPerformance[key].grades[grade]) {
          locationPerformance[key].grades[grade] = 0
        }
        locationPerformance[key].grades[grade] += record.grades[grade] || 0
      })
    })

    // Calculate excellence rates and create rankings by year
    const locationRankings = {}
    Object.values(locationPerformance).forEach(record => {
      if (!locationRankings[record.year]) {
        locationRankings[record.year] = []
      }

      const excellenceCount =
        (record.grades['Division 1'] || 0) + (record.grades['Division 2'] || 0)
      const excellenceRate =
        record.total > 0 ? (excellenceCount / record.total) * 100 : 0

      locationRankings[record.year].push({
        location: record.location,
        excellenceRate,
        total: record.total,
      })
    })

    // Sort and rank within each year
    Object.keys(locationRankings).forEach(year => {
      locationRankings[year].sort((a, b) => b.excellenceRate - a.excellenceRate)
      locationRankings[year].forEach((location, index) => {
        location.rank = index + 1
      })
    })

    const years = Object.keys(locationRankings).sort(
      (a, b) => parseInt(a) - parseInt(b)
    )
    if (!years.length) return {}

    const locations = new Set()
    Object.values(locationRankings).forEach(yearData => {
      yearData.forEach(location => {
        locations.add(location.location)
      })
    })

    const locationList = Array.from(locations).sort()
    const colors = [
      '#3b82f6',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#ec4899',
      '#06b6d4',
      '#84cc16',
    ]

    return {
      title: {
        show: false,
        text: chartTitle,
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            const rank = parseInt(param.value) || 0
            const suffix =
              rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'
            result += `${param.marker} ${param.seriesName}: ${rank}${suffix} place<br/>`
          })
          return result
        },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      legend: {
        data: locationList.slice(0, 10), // Show more locations for better insight
        top: 40,
        type: 'scroll',
        textStyle: {
          fontSize: 11,
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '20%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Ranking Position',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        inverse: true, // Lower rank numbers at top
        min: 1,
        max: Math.max(
          ...Object.values(locationRankings).flatMap(year =>
            year.map(d => d.rank)
          ),
          1
        ),
        splitLine: {
          show: false,
        },
      },
      series: locationList.slice(0, 10).map((location, index) => ({
        name: location,
        type: 'line',
        smooth: true,
        data: years.map(year => {
          const locationData = locationRankings[year]?.find(
            d => d.location === location
          )
          return locationData ? locationData.rank : null
        }),
        lineStyle: {
          color: colors[index % colors.length],
          width: 3,
        },
        itemStyle: {
          color: colors[index % colors.length],
        },
        symbol: 'circle',
        symbolSize: 8,
        connectNulls: false,
        emphasis: { focus: 'series' },
      })),
    }
  }, [
    processedData,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
  ])

  if (!data || !data.length) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📊</div>
          <p>No PLE performance data available</p>
          <p className="text-sm mt-2">
            Please ensure PLE analysis data is available for the selected
            location
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Summary Statistics */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📈 PLE Performance Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {yearlyData.length > 0 &&
            (() => {
              const latest = yearlyData[yearlyData.length - 1]
              const passCount = PASS_GRADES.reduce(
                (sum, grade) => sum + (latest.grades[grade] || 0),
                0
              )
              const excellenceCount = EXCELLENCE_GRADES.reduce(
                (sum, grade) => sum + (latest.grades[grade] || 0),
                0
              )
              const passRate =
                latest.total > 0 ? (passCount / latest.total) * 100 : 0
              const excellenceRate =
                latest.total > 0 ? (excellenceCount / latest.total) * 100 : 0

              return (
                <>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">
                      {latest.total.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      Total Students ({latest.year})
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-green-600">
                      {passRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      Pass Rate (Div 1-3)
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">
                      {excellenceRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      Excellence Rate (Div 1-2)
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-indigo-600">
                      {yearlyData.length}
                    </div>
                    <div className="text-sm text-gray-600">Years of Data</div>
                  </div>
                </>
              )
            })()}
        </div>
      </div>

      {/* Performance Trends Over Time */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            PLE Performance Trends Over Time
          </h3>
          <p className="text-sm text-gray-600">
            Track overall PLE performance changes over time across all grade
            divisions to monitor education quality trends
          </p>
        </div>
        <ReactECharts
          option={performanceTrendsOption}
          style={{ height: '500px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Gender Performance Gap */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Gender Performance Gap Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Monitor gender equity in education by comparing male and female
            excellence rates and identifying performance gaps
          </p>
        </div>
        <ReactECharts
          option={genderGapOption}
          style={{ height: '400px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* School Ownership Impact */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            School Ownership Impact on Performance
          </h3>
          <p className="text-sm text-gray-600">
            Compare performance between government and private schools to inform
            education policy and resource allocation decisions
          </p>
        </div>
        <ReactECharts
          option={ownershipImpactOption}
          style={{ height: '400px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Dynamic Location Performance Ranking Evolution */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Performance Ranking Evolution
          </h3>
          <p className="text-sm text-gray-600">
            Track how different locations rank against each other over time to
            identify consistently high-performing vs declining areas
          </p>
        </div>
        <ReactECharts
          option={locationRankingOption}
          style={{ height: '500px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Pass Rate Trends by Location Heatmap */}
      {passRateHeatmapData && passRateHeatmapData.locations.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            📍 Pass Rate Trends by{' '}
            {locationLevel === 'district'
              ? 'District'
              : locationLevel === 'subcounty'
                ? 'Subcounty'
                : locationLevel === 'parish'
                  ? 'Parish'
                  : 'Village'}
          </h3>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Identify locations with consistently high pass rates (Division
              1-3) versus areas needing educational support
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left text-sm font-medium text-gray-700 p-2">
                    Location
                  </th>
                  {passRateHeatmapData.years.map(year => (
                    <th
                      key={year}
                      className="text-center text-sm font-medium text-gray-700 p-2 min-w-20"
                    >
                      {year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {passRateHeatmapData.locations.map(location => (
                  <tr key={location} className="border-t">
                    <td className="text-sm font-medium text-gray-900 p-2">
                      {location}
                    </td>
                    {passRateHeatmapData.years.map(year => {
                      const cellData = passRateHeatmapData.data.find(
                        d => d.location === location && d.year === year
                      )
                      return (
                        <td key={year} className="text-center p-2">
                          <div
                            className="w-16 h-8 rounded flex items-center justify-center text-white text-xs font-medium mx-auto"
                            style={{
                              backgroundColor: cellData?.color || '#6b7280',
                            }}
                            title={`${location}, ${year}: Pass Rate ${cellData?.passRate?.toFixed(1) || 0}%`}
                          >
                            {cellData?.passRate?.toFixed(0) || 0}%
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              Pass rate (Division 1-3) across locations and years
            </p>
            <p className="flex items-center space-x-4 mt-2">
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span>
                Excellent (≥80%)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-yellow-500 rounded mr-1"></span>
                Moderate (60-79%)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-red-500 rounded mr-1"></span>
                Poor (&lt;60%)
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Failure Rate Hotspot Analysis Heatmap */}
      {failureRateHeatmapData &&
        failureRateHeatmapData.locations.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              🔥 Failure Rate Hotspot Analysis by{' '}
              {locationLevel === 'district'
                ? 'District'
                : locationLevel === 'subcounty'
                  ? 'Subcounty'
                  : locationLevel === 'parish'
                    ? 'Parish'
                    : 'Village'}
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Identify high-risk areas with elevated failure rates (Division
                U/X) that require priority intervention and support
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="text-left text-sm font-medium text-gray-700 p-2">
                      Location
                    </th>
                    {failureRateHeatmapData.years.map(year => (
                      <th
                        key={year}
                        className="text-center text-sm font-medium text-gray-700 p-2 min-w-20"
                      >
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {failureRateHeatmapData.locations.map(location => (
                    <tr key={location} className="border-t">
                      <td className="text-sm font-medium text-gray-900 p-2">
                        {location}
                      </td>
                      {failureRateHeatmapData.years.map(year => {
                        const cellData = failureRateHeatmapData.data.find(
                          d => d.location === location && d.year === year
                        )
                        return (
                          <td key={year} className="text-center p-2">
                            <div
                              className="w-16 h-8 rounded flex items-center justify-center text-white text-xs font-medium mx-auto"
                              style={{
                                backgroundColor: cellData?.color || '#6b7280',
                              }}
                              title={`${location}, ${year}: Failure Rate ${cellData?.failureRate?.toFixed(1) || 0}%`}
                            >
                              {cellData?.failureRate?.toFixed(0) || 0}%
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p className="font-medium">
                Failure rate (Division U/X) - intervention priority areas
              </p>
              <p className="flex items-center space-x-4 mt-2">
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span>
                  Low Risk (&lt;10%)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-yellow-500 rounded mr-1"></span>
                  Moderate Risk (10-20%)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-red-500 rounded mr-1"></span>
                  High Risk (≥20%)
                </span>
              </p>
            </div>
          </div>
        )}

      {/* School-Level Performance Trajectories */}
      {schoolTrajectoriesData.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            🏫 School Performance Trajectories (Top 12 by Growth Rate)
          </h3>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Examine individual school excellence rate trends over time to
              identify the highest-improving schools and successful practices
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {schoolTrajectoriesData.map(schoolData => (
              <div key={schoolData.school} className="border rounded-lg p-3">
                <div className="mb-2">
                  <h4
                    className="text-sm font-medium text-gray-900 truncate"
                    title={schoolData.school}
                  >
                    {schoolData.school}
                  </h4>
                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        schoolData.isImproving
                          ? 'bg-green-500'
                          : schoolData.isStable
                            ? 'bg-blue-500'
                            : 'bg-red-500'
                      }`}
                    ></span>
                    <span>
                      {schoolData.growthRate > 0 ? '+' : ''}
                      {schoolData.growthRate.toFixed(1)}% per year
                    </span>
                  </div>
                </div>
                <div className="h-20">
                  <ReactECharts
                    option={{
                      grid: { left: 10, right: 10, top: 5, bottom: 20 },
                      xAxis: {
                        type: 'category',
                        data: schoolData.trajectory.map(t => t.year),
                        axisLabel: { fontSize: 10 },
                        axisTick: { show: false },
                        axisLine: { show: false },
                        splitLine: {
                          show: false,
                        },
                      },
                      yAxis: {
                        type: 'value',
                        min: 0,
                        max: 100,
                        axisLabel: { show: false },
                        axisTick: { show: false },
                        axisLine: { show: false },
                        splitLine: { show: false },
                      },
                      series: [
                        {
                          type: 'line',
                          data: schoolData.trajectory.map(
                            t => t.excellenceRate
                          ),
                          lineStyle: {
                            color: schoolData.isImproving
                              ? '#10b981'
                              : schoolData.isStable
                                ? '#3b82f6'
                                : '#ef4444',
                            width: 2,
                          },
                          itemStyle: {
                            color: schoolData.isImproving
                              ? '#10b981'
                              : schoolData.isStable
                                ? '#3b82f6'
                                : '#ef4444',
                          },
                          symbol: 'circle',
                          symbolSize: 4,
                          smooth: true,
                        },
                      ],
                      tooltip: {
                        trigger: 'axis',
                        formatter: function (params) {
                          const point = params[0]
                          return `${point.axisValue}: ${point.value.toFixed(1)}% excellence rate`
                        },
                      },
                    }}
                    style={{ height: '100%', width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    theme="ug-dashboard"
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1 text-center">
                  {schoolData.firstRate.toFixed(0)}% →{' '}
                  {schoolData.lastRate.toFixed(0)}% excellence
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              Excellence rate trends (Division 1-2) for highest-improving
              schools
            </p>
            <p className="flex items-center space-x-4 mt-2">
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                Improving (positive growth)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                Stable (±1% per year)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                Declining (negative growth)
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Data Insights */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">📋 Key Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <strong>Performance Trends:</strong> Track how overall PLE
            performance is changing over time across all grade divisions.
          </div>
          <div>
            <strong>Gender Equity:</strong> Monitor gender gaps in excellence
            rates (Division 1-2) to identify disparities.
          </div>
          <div>
            <strong>School Type Impact:</strong> Compare government vs private
            school performance to inform policy decisions.
          </div>
          <div>
            <strong>Location Rankings:</strong> Track performance evolution of
            sub-locations (subcounties, parishes, villages) to identify
            improving vs declining areas.
          </div>
          <div>
            <strong>Excellence Focus:</strong> Division 1-2 rates indicate
            students ready for secondary education.
          </div>
          <div>
            <strong>Pass Rate Analysis:</strong> Division 1-3 represents
            students meeting minimum standards for progression.
          </div>
        </div>
      </div>
    </div>
  )
}

PLETrends.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      district: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      school_name: PropTypes.string,
      gender: PropTypes.oneOf(['Male', 'Female', '']),
      grade: PropTypes.oneOf([
        'Division 1',
        'Division 2',
        'Division 3',
        'Division 4',
        'Division U',
        'Division X',
      ]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ownership: PropTypes.oneOf(['Government', 'Private', '']),
      location_code: PropTypes.string,
    })
  ),
}

export default PLETrends
